// Reads pixel dimensions, and a JPEG's EXIF orientation, straight out of an image
// header, without decoding the image. Decoding is what kills us: a 48MP photo costs ~195MB of RGBA, which is
// enough to OOM a 512MB container. Only JPEG and PNG are recognised - they are
// the only formats @react-pdf can embed anyway. Anything else (and anything
// truncated or malformed) returns null instead of throwing; callers decide what
// an unknown size means for them.

export interface ImageSize {
  width: number
  height: number
}

// How many leading bytes we look at before giving up and reading the whole blob.
// A JPEG's SOF marker sits after all APP segments (EXIF, ICC, thumbnails), so it
// can be surprisingly deep, but never this deep in practice.
const HEADER_PROBE_SIZE = 256 * 1024

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

export function getMegapixels({ width, height }: ImageSize) {
  return (width * height) / 1_000_000
}

export function readImageSize(bytes: Uint8Array): ImageSize | null {
  return readPngSize(bytes) ?? readJpegSize(bytes)
}

export async function readImageSizeFromBlob(blob: Blob): Promise<ImageSize | null> {
  const probeSize = Math.min(blob.size, HEADER_PROBE_SIZE)
  const probe = new Uint8Array(await blob.slice(0, probeSize).arrayBuffer())

  const size = readImageSize(probe)
  if (size || probeSize === blob.size) return size

  // The header we wanted was past the probe - pay for the full read.
  return readImageSize(new Uint8Array(await blob.arrayBuffer()))
}

// PNG: 8 byte signature, then the IHDR chunk whose payload starts with
// width and height as big-endian uint32s.
function readPngSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < 24) return null
  if (PNG_SIGNATURE.some((byte, i) => bytes[i] !== byte)) return null
  if (String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]) !== 'IHDR') return null

  const width = readUint32(bytes, 16)
  const height = readUint32(bytes, 20)
  return isValidSize(width, height) ? { width, height } : null
}

// JPEG: the first start-of-frame segment carries height and width as big-endian
// uint16s after a one byte sample precision.
function readJpegSize(bytes: Uint8Array): ImageSize | null {
  for (const segment of jpegSegments(bytes)) {
    if (!isStartOfFrame(segment.marker)) continue
    // precision(1) + height(2) + width(2)
    if (segment.payload + 5 >= bytes.length) return null
    const height = readUint16(bytes, segment.payload + 1)
    const width = readUint16(bytes, segment.payload + 3)
    return isValidSize(width, height) ? { width, height } : null
  }
  return null
}

/**
 * The EXIF orientation (1-8) of a JPEG, read from its header bytes; 1 when there is none or the
 * bytes are not a JPEG. Phones store a portrait photo as landscape pixels plus this tag.
 */
export function readJpegOrientation(bytes: Uint8Array): number {
  for (const segment of jpegSegments(bytes)) {
    if (segment.marker !== APP1 || !isExif(bytes, segment.payload)) continue
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const end = Math.min(segment.end, bytes.length)
    return readTiffOrientation(view, segment.payload + EXIF_HEADER.length, end) ?? 1
  }
  return 1
}

/** The size an image shows at: orientations 5-8 turn it by 90°. */
export function orientedSize(size: ImageSize, orientation: number): ImageSize {
  return orientation >= 5 && orientation <= 8 ? { width: size.height, height: size.width } : size
}

interface JpegSegment {
  marker: number
  /** Where the segment's payload starts, right after its length. */
  payload: number
  /** Where the segment ends, as its length says; may lie past truncated bytes. */
  end: number
}

// The marker segments of a JPEG header, in order, up to the start of scan. Stops quietly at
// anything that is not a JPEG or is malformed.
function* jpegSegments(bytes: Uint8Array): Generator<JpegSegment> {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return

  let offset = 2
  while (offset + 1 < bytes.length) {
    // Segments are padded with any number of 0xff fill bytes.
    if (bytes[offset] !== 0xff) return
    let marker = bytes[offset + 1]
    while (marker === 0xff && offset + 2 < bytes.length) {
      offset += 1
      marker = bytes[offset + 1]
    }
    offset += 2

    // Standalone markers (padding, restart markers, SOI) carry no payload.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue
    // Start of scan or end of image - we are past every header.
    if (marker === 0xda || marker === 0xd9) return

    if (offset + 1 >= bytes.length) return
    const segmentLength = readUint16(bytes, offset)
    if (segmentLength < 2) return

    yield { marker, payload: offset + 2, end: offset + segmentLength }
    offset += segmentLength
  }
}

const APP1 = 0xe1
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0, 0] // "Exif\0\0"
const ORIENTATION_TAG = 0x0112

function isExif(bytes: Uint8Array, offset: number) {
  return EXIF_HEADER.every((byte, i) => bytes[offset + i] === byte)
}

// The orientation entry of IFD0 in the TIFF structure an EXIF segment holds.
function readTiffOrientation(view: DataView, tiff: number, end: number): number | undefined {
  if (tiff + 8 > end) return undefined
  const byteOrder = view.getUint16(tiff)
  if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return undefined
  const little = byteOrder === 0x4949

  const ifd = tiff + view.getUint32(tiff + 4, little)
  if (ifd + 2 > end) return undefined
  const entries = view.getUint16(ifd, little)
  for (let i = 0; i < entries; i++) {
    const entry = ifd + 2 + i * 12
    if (entry + 12 > end) return undefined
    if (view.getUint16(entry, little) === ORIENTATION_TAG) {
      const orientation = view.getUint16(entry + 8, little)
      return orientation >= 1 && orientation <= 8 ? orientation : undefined
    }
  }
  return undefined
}

// SOF0-SOF15, minus the markers that reuse the 0xc0-0xcf range for other things:
// 0xc4 (define Huffman table), 0xc8 (JPEG extension), 0xcc (define arithmetic
// coding conditioning).
function isStartOfFrame(marker: number) {
  if (marker < 0xc0 || marker > 0xcf) return false
  return marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
}

function isValidSize(width: number, height: number) {
  return width > 0 && height > 0
}

function readUint16(bytes: Uint8Array, offset: number) {
  return (bytes[offset] << 8) | bytes[offset + 1]
}

function readUint32(bytes: Uint8Array, offset: number) {
  return (
    bytes[offset] * 0x1000000 +
    ((bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3])
  )
}
